import { Epic } from 'redux-observable';
import { filter, mergeMap } from 'rxjs/operators';
import {from, Observable, of, withLatestFrom} from 'rxjs';
import { Action } from 'redux';
import {
  addFiles,
  removeFiles,
  MediaType,
  deleteSelected,
  createThumbnail,
  updateThumbnail
} from '../slices';
import {
  generateImageThumbnailFromFile,
  generateVideoThumbnailFromFile,
  parseMedia,
} from '../../utils';
import heic2any from 'heic2any';
import { selectFiles } from "../selectors";

export const addThumbnailEpic: Epic<Action, Action, any, any> = (action$) =>
  action$.pipe(
    filter(addFiles.match),
    mergeMap((action) => {
      return new Observable<Action>((observer) => {
        action.payload.forEach(file => {
          const mediaType = parseMedia(file.type);
          return observer.next(createThumbnail({
            name: file.name,
            mediaType
          }))
        })
      });
    })
  );

export const generateThumbnailsEpic: Epic<Action, Action, any, any> = (action$, state$) =>
  action$.pipe(
    filter(createThumbnail.match),
    withLatestFrom(state$),
    mergeMap(([action, state]) => {
      const thumbnail = action.payload
      const file = selectFiles(state).find(f => f.name === thumbnail.name);

      return from(
        (async () => {
          try {
            if (!file) return updateThumbnail({
              name: thumbnail.name,
              thumbnailUrl: null,
            });
            const mediaType = thumbnail.mediaType;

            let thumbnailUrl: string;
            let fileForThumbnail = file;

            if (mediaType === MediaType.IMAGE) {
              // Handle HEIC conversion if needed
              const isHeic = file.type === 'image/heic' || file.name.toLowerCase().endsWith('.heic');

              if (isHeic) {
                try {
                  const convertedBlob = await heic2any({
                    blob: file,
                    toType: 'image/jpeg',
                    quality: 0.8
                  });

                  const blob = Array.isArray(convertedBlob) ? convertedBlob[0] : convertedBlob;
                  fileForThumbnail = new File([blob], file.name.replace(/\.heic$/i, '.jpg'), {
                    type: 'image/jpeg'
                  });
                } catch (error) {
                  console.error('HEIC conversion failed, using original:', error);
                }
              }

              thumbnailUrl = await generateImageThumbnailFromFile(fileForThumbnail, 250, 0.8);
            } else if (mediaType === MediaType.VIDEO) {
              // Video
              thumbnailUrl = await generateVideoThumbnailFromFile(file, 250, 0.8, 1);
            } else {
              return updateThumbnail({
                name: thumbnail.name,
                thumbnailUrl: null,
              });
            }

            return updateThumbnail({
              name: thumbnail.name,
              thumbnailUrl,
            });
          } catch (error) {
            console.error(`Failed to generate thumbnail for ${thumbnail.name}:`, error);
            return updateThumbnail({
              name: thumbnail.name,
              thumbnailUrl: null,
            });
          }
        })()
      ).pipe(
        filter(
          (
            action
          ): action is ReturnType<typeof updateThumbnail> =>
            action !== null
        )
      );
    })
  );

export const syncDeleteEpic: Epic<Action, Action, any, any> = (action$) =>
  action$.pipe(
    filter(deleteSelected.match),
    mergeMap((action) => {
      // Get file names from action payload
      const fileNamesToDelete = action.payload;

      if (!fileNamesToDelete || fileNamesToDelete.length === 0) {
        return of(); // Empty observable if nothing to delete
      }
      
      // Also remove files from files slice
      return of(removeFiles(fileNamesToDelete));
    })
  );
